import {
  and,
  eq,
  type PowerSyncDrizzleDB,
  workspace,
  workspace_member,
} from "@time-tracker/powersync";
import { liveWorkspaceMemberFilters } from "@time-tracker/shared";
import { generateId } from "@fleet/utils";

/**
 * In-flight promises of `ensureWorkspace`, indexed by user. Without this,
 * multiple callers (timeline, pulse-timer, tags, projects, team) may
 * trigger it almost simultaneously and, during PowerSync's reconciliation window
 * —in which the optimistic `workspace_member` row has not yet returned confirmed
 * from the server and the live-query sees it empty— each call would create a new
 * workspace. Coalescing concurrent calls into a single promise avoids that
 * storm of duplicate workspaces/PUTs.
 */
const inFlight = new Map<string, Promise<string>>();

async function findOwnWorkspace(
  dbInstance: PowerSyncDrizzleDB,
  userId: string,
): Promise<string | undefined> {
  const membership = await dbInstance
    .select({ workspace_id: workspace_member.workspace_id })
    .from(workspace_member)
    .where(and(eq(workspace_member.user_id, userId), ...liveWorkspaceMemberFilters()))
    .limit(1);
  return membership[0]?.workspace_id ?? undefined;
}

/**
 * Slug of a personal workspace. Uses the full id: its first 8 hex characters are
 * the top bits of a UUIDv7 timestamp, so a shorter prefix collides between users.
 */
export function personalWorkspaceSlug(workspaceId: string): string {
  return `personal-${workspaceId}`;
}

async function createPersonalWorkspace(
  dbInstance: PowerSyncDrizzleDB,
  userId: string,
): Promise<string> {
  const workspaceId = generateId();
  const now = new Date().toISOString();

  // `workspace` and `workspace_member` must upload together and in order: server
  // only allows bootstrap (workspace INSERT without yet being a member) when
  // creator adds themselves as a member in the same logical operation. A single
  // PowerSync writeTransaction groups both INSERTs in ONE single CRUD
  // transaction → they reach server in same batch and in this order.
  await dbInstance.transaction(async (tx) => {
    await tx.insert(workspace).values({
      id: workspaceId,
      name: "Personal Workspace",
      // Slug has a global UNIQUE index in Postgres (workspace_slug_idx),
      // so it CANNOT be a shared constant: first user to
      // sync wins "personal-workspace" and subsequent INSERTs from others are
      // rejected due to collision → workspace never reaches server and, in cascade,
      // neither do workspace_member (FK) / project / time_entry. It is derived from
      // the whole id: a prefix of a UUIDv7 is its timestamp, so two users who create
      // a workspace in the same ~65 s window would collide (see personalWorkspaceSlug).
      slug: personalWorkspaceSlug(workspaceId),
      created_at: now,
      updated_at: now,
    });
    await tx.insert(workspace_member).values({
      id: generateId(),
      workspace_id: workspaceId,
      user_id: userId,
      role: "owner",
      created_at: now,
      updated_at: now,
    });
  });

  return workspaceId;
}

/**
 * Ensures user has at least one workspace: returns first
 * one where they are a member, or creates their "Personal Workspace". Does not seed demo
 * data: projects, teammates, and events are created by real user from UI.
 */
export async function ensureWorkspace(
  dbInstance: PowerSyncDrizzleDB,
  userId: string,
): Promise<string> {
  const existing = await findOwnWorkspace(dbInstance, userId);
  if (existing) {
    return existing;
  }

  const pending = inFlight.get(userId);
  if (pending) {
    return pending;
  }

  const task = (async () => {
    // Re-check inside lock: another call could have created workspace
    // between initial SELECT and acquiring lock.
    const again = await findOwnWorkspace(dbInstance, userId);
    if (again) {
      return again;
    }
    return createPersonalWorkspace(dbInstance, userId);
  })();

  inFlight.set(userId, task);
  try {
    return await task;
  } finally {
    inFlight.delete(userId);
  }
}
